import { useLangGraphStream } from '@/hooks/useLangGraphStream'
import { useDiscoveryStore } from '@/state/discoveryStore'
import type { DiscoveryStepKey, DiscoveryStepStatus } from '@/state/discoveryStore'
import type { Provider, DiscoverySummary } from '@/lib/types'

export interface DiscoverProvidersParams {
  specialties: string[]
  city: string
  state: string
  radius?: number
  enumerationType?: string
}

export function useDiscoverProviders() {
  const { stream: streamSSE, isStreaming } = useLangGraphStream({
    onEvent: (type, data) => {
      const store = useDiscoveryStore.getState()

      if (type === 'step') {
        try {
          const parsed = JSON.parse(data) as { step?: string; status?: string }
          if (parsed.step && parsed.status) {
            store.setStep(
              parsed.step as DiscoveryStepKey,
              parsed.status as DiscoveryStepStatus,
            )
          }
        } catch {
          // Ignore malformed JSON
        }
        return
      }

      if (type === 'results') {
        try {
          const parsed = JSON.parse(data) as {
            providers?: Provider[]
            summary?: DiscoverySummary
          }
          if (parsed.providers) {
            store.setProviders(parsed.providers)
          }
          if (parsed.summary) {
            store.setSummary(parsed.summary)
          }
        } catch {
          // Ignore malformed JSON
        }
        return
      }

      if (type === 'error') {
        try {
          const parsed = JSON.parse(data) as { message?: string }
          store.setStatus('error')
          store.setErrorMessage(parsed.message ?? 'Discovery failed')
        } catch {
          store.setStatus('error')
          store.setErrorMessage(data || 'Discovery failed')
        }
        return
      }

      if (type === 'done') {
        const current = useDiscoveryStore.getState()
        if (current.status !== 'error') {
          store.setStatus('complete')
        }
        return
      }
    },
    onDone: () => {
      const store = useDiscoveryStore.getState()
      if (store.status !== 'error') {
        store.setStatus('complete')
      }
    },
    onError: (message) => {
      const store = useDiscoveryStore.getState()
      store.setStatus('error')
      store.setErrorMessage(message)
    },
  })

  const discover = async (clientId: string, params: DiscoverProvidersParams) => {
    const store = useDiscoveryStore.getState()
    store.reset()
    store.setStatus('running')

    const body: Record<string, unknown> = {
      specialties: params.specialties,
      city: params.city,
      state: params.state,
    }
    if (params.radius !== undefined) body.radius = params.radius
    if (params.enumerationType) body.enumerationType = params.enumerationType

    await streamSSE(
      `${import.meta.env.VITE_API_BASE_URL}/api/clients/${clientId}/discover-providers-stream`,
      body,
    )
  }

  return { discover, isStreaming }
}
